import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { EMPTY_TREE_CODEBASE_ID } from "@/utils/constants";
import { rebuildLookupsFromNavTree } from "@/utils/navTreeCompression";
import { TempoElement } from "@/utils/tempoElement";
import type { NavTreeNode } from "@/utils/types";

/**
 * Efficiently update all nav tree lookups by diffing changes instead of full traversal
 */

const mostCommonValue = (values: string[]): string | undefined => {
  if (values.length === 0) {
    return undefined;
  }

  const count: Record<string, number> = {};
  for (const value of values) {
    count[value] = (count[value] || 0) + 1;
  }
  return Object.entries(count).sort((a, b) => b[1] - a[1])?.[0]?.[0];
};

export interface NavTreeUpdates {
  elementKeyLookup: { [elementKey: string]: NavTreeNode };
  domUniquePathElementKeyLookup: { [elementKey: string]: NavTreeNode };
  codebaseIdLookup: { [codebaseId: string]: NavTreeNode[] };
  codebaseIds: Set<string>;
  navTree: NavTreeNode;
}

export const getEmptyLookups = (storyboardId: string): NavTreeUpdates => {
  return {
    elementKeyLookup: {},
    domUniquePathElementKeyLookup: {},
    codebaseIdLookup: {},
    codebaseIds: new Set(),
    navTree: {} as NavTreeNode,
  };
};

export const getCurrentLookups = (storyboardId: string): NavTreeUpdates => {
  const {
    elementKeyToNavNodeLookup,
    domUniquePathElementKeyLookup,
    sbIdToCodebaseIdToNavNodeLookup,
    storyboardIdToCodebaseIds,
    storyboardToNavTree,
  } = useCanvasStore.getState();

  // Only get the element keys and dom unique path element keys that contain the storyboardId
  const elementKeyToNavNodeLookupForSb = Object.keys(elementKeyToNavNodeLookup)
    .filter((key) => key.includes(storyboardId))
    .reduce((acc, key) => {
      acc[key] = elementKeyToNavNodeLookup[key];
      return acc;
    }, {});
  const domUniquePathElementKeyLookupForSb = Object.keys(
    domUniquePathElementKeyLookup,
  )
    .filter((key) => key.includes(storyboardId))
    .reduce((acc, key) => {
      acc[key] = domUniquePathElementKeyLookup[key];
      return acc;
    }, {});

  return {
    elementKeyLookup: { ...(elementKeyToNavNodeLookupForSb || {}) },
    domUniquePathElementKeyLookup: {
      ...(domUniquePathElementKeyLookupForSb || {}),
    },
    codebaseIdLookup: {
      ...(sbIdToCodebaseIdToNavNodeLookup[storyboardId] || {}),
    },
    codebaseIds: new Set([...(storyboardIdToCodebaseIds[storyboardId] || [])]),

    // Do not create a copy here as it could break all references between children and parents
    navTree: storyboardToNavTree[storyboardId] ?? {
      children: [],
      tempoElement: TempoElement.empty(),
      name: "",
    },
  };
};

// Helper to update all store lookups and nav tree
export const updateAllStoreLookups = (
  storyboardId: string,
  navTreeUpdates: Partial<NavTreeUpdates>,
  recreateLookups: boolean,
) => {
  const {
    setElementKeyToNavNodeLookup,
    setDomUniquePathElementKeyLookup,
    setCodebaseIdToNavNodeLookup,
    setStoryboardIdToCodebaseIds,
    setNavTreeForStoryboard,
  } = useCanvasStore.getState();

  if (!navTreeUpdates?.navTree) {
    return;
  }

  if (recreateLookups) {
    const newNavTree = navTreeUpdates.navTree;
    const lookups = rebuildLookupsFromNavTree(newNavTree, storyboardId);

    setElementKeyToNavNodeLookup(storyboardId, lookups.elementKeyLookup);
    navTreeUpdates.elementKeyLookup = lookups.elementKeyLookup;

    setDomUniquePathElementKeyLookup(
      storyboardId,
      lookups.domUniquePathElementKeyLookup,
    );
    navTreeUpdates.domUniquePathElementKeyLookup =
      lookups.domUniquePathElementKeyLookup;

    setCodebaseIdToNavNodeLookup(storyboardId, lookups.codebaseIdLookup);
    navTreeUpdates.codebaseIdLookup = lookups.codebaseIdLookup;

    setStoryboardIdToCodebaseIds(storyboardId, lookups.codebaseIds);
    navTreeUpdates.codebaseIds = lookups.codebaseIds;
  }

  setNavTreeForStoryboard(storyboardId, navTreeUpdates.navTree);
};

export const fixUniquePathsForAllChildren = (parent: NavTreeNode) => {
  // Update parent references for wrapped nodes
  const updateChild = (
    node: NavTreeNode,
    index: number,
    currentUniquePath: string,
    currentDomUniquePath: string,
  ) => {
    node.level = (node.parent?.level || 0) + 1;

    let newDomUniquePath = currentDomUniquePath;
    if (node.domUniquePath) {
      // For nodes that have the domUnique path, update it and increment
      newDomUniquePath = `${newDomUniquePath}-${index}`;
    }

    // All nodes have a unique path, so we can just update it
    const newUniquePath = `${currentUniquePath}-${index}`;

    node.domUniquePath = newDomUniquePath;
    node.tempoElement = new TempoElement(
      node.tempoElement.codebaseId,
      node.tempoElement.storyboardId,
      newUniquePath,
    );

    let domUniquePathFromChildren: string | null = null;
    node.children?.forEach((child, index) => {
      const domUniquePathFound = updateChild(
        child,
        index,
        newUniquePath,
        newDomUniquePath,
      );
      if (domUniquePathFound) {
        domUniquePathFromChildren = domUniquePathFound;
      }
    });

    if (!domUniquePathFromChildren) {
      domUniquePathFromChildren = currentDomUniquePath + "-0";
    }

    if (node.isComponent && domUniquePathFromChildren) {
      node.domUniquePathForComponent = domUniquePathFromChildren;
    }

    return node.domUniquePath ?? domUniquePathFromChildren;
  };

  // If this is the top-level node, look through all storyboard nav trees for the likely values for the top-level node
  let domUniquePath = parent.domUniquePath;
  if (!parent.parent) {
    parent.level = 0;

    const storyboardToNavTree = useCanvasStore.getState().storyboardToNavTree;
    const allDomUniquePaths = Object.values(storyboardToNavTree)
      .map((navTree) => {
        return navTree?.domUniquePath;
      })
      .filter((path) => path !== undefined);
    const mostCommonDomUniquePath = mostCommonValue(allDomUniquePaths);

    const allUniquePaths = Object.values(storyboardToNavTree)
      .map((navTree) => {
        return navTree?.tempoElement?.uniquePath;
      })
      .filter((path) => path !== undefined);
    const mostCommonUniquePath = mostCommonValue(allUniquePaths);

    if (mostCommonDomUniquePath) {
      parent.domUniquePath = mostCommonDomUniquePath;
    }

    if (mostCommonUniquePath) {
      parent.tempoElement = new TempoElement(
        parent.tempoElement.codebaseId,
        parent.tempoElement.storyboardId,
        mostCommonUniquePath,
      );
    }

    domUniquePath = parent.domUniquePath ?? "0";
  } else {
    parent.level = (parent.parent?.level || 0) + 1;

    // The parent may not have a domUniquePath, search the tree up for the first one
    if (!domUniquePath) {
      let currentNode: NavTreeNode | undefined = parent;
      while (currentNode && !currentNode.domUniquePath) {
        currentNode = currentNode.parent;
      }
      domUniquePath = currentNode?.domUniquePath || "0";
    }
  }

  parent.children?.forEach((child, index) => {
    updateChild(child, index, parent.tempoElement.uniquePath, domUniquePath);
  });
  return domUniquePath;
};

export const isNavTreeEmpty = (navTree: NavTreeNode | undefined) => {
  return (
    !navTree ||
    !navTree.tempoElement ||
    navTree.tempoElement.codebaseId === EMPTY_TREE_CODEBASE_ID
  );
};
